<template>
  <b-modal
    ref="installRecipeModal"
    centered
    size="md"
    title="Install recipe on server"
    hide-header-close
    scrollable
    footer-bg-variant=""
    @hidden="
      unselectRecipe(),
      unselectServer(),
      unselectRecipeVersion()
    ">
    <no-data-found
      v-if="recipesEmpty"
      :text="'No recipes found'" />
    <b-form
      v-else
      @submit.stop.prevent>
      <b-form-group
        id="recipe-server"
        label="Recipe"
        label-for="recipe-input">
        <b-form-select v-model="selectedRecipe">
          <template #first>
            <b-form-select-option
              :value="defaultSelectedRecipe"
              disabled>
              Please select recipe
            </b-form-select-option>
          </template>
          <b-form-select-option
            v-for="recipe in recipesList"
            :key="`recipe-${recipe._id}`"
            :value="recipe">
            {{ recipe.name }} [{{ recipe.recipes_version_count }} {{ 'version' | pluralize(recipe.recipes_version_count) }}]
          </b-form-select-option>
        </b-form-select>
      </b-form-group>

      <b-form-group
        id="recipe-server"
        label="Script Version"
        label-for="recipe-input">
        <b-form-select
          v-model="selectedRecipeVersion"
          :disabled="recipeVersionsPending(selectedRecipe._id)">
          <template #first>
            <b-form-select-option
              :value="defaultSelectedRecipeVersion"
              disabled>
              Please select script version
            </b-form-select-option>
          </template>
          <b-form-select-option
            v-for="version in recipeVersions(selectedRecipeId)"
            :key="`version-${version._id}`"
            :value="version">
            {{ beautifiedVersionTitle(version) }}
          </b-form-select-option>
        </b-form-select>
      </b-form-group>

      <div class="font-weight-bold mb-2">Server Details</div>
      <b-list-group
        v-if="selectedServerId !== null"
        class="mb-0">
        <b-list-group-item class="d-flex align-items-center">
          Name
          <span class="ml-auto">{{ selectedServer.name }}</span>
        </b-list-group-item>
        <b-list-group-item class="d-flex align-items-center">
          Location
          <span class="ml-auto">
            <div class="d-flex align-items-center">
              <img
                :src="countryFlag(selectedServer.country)"
                :alt="selectedServer.country"
                class="w-20px mr-2 no-select rounded-sm bg-light border border-light rounded-sm" />
              <span
                class="d-inline-block mr-1 text-truncate"
                style="max-width: 180px;">{{ countryName(selectedServer.country) }}, {{ selectedServer.location }}</span>
            </div>
          </span>
        </b-list-group-item>
        <b-list-group-item class="d-flex align-items-center">
          IP
          <span class="ml-auto">{{ selectedServer.ip }}</span>
        </b-list-group-item>
        <b-list-group-item class="d-flex align-items-center">
          Status
          <span class="ml-auto font-size-xs">
            <b-badge :variant="serverStatusBadge(selectedServer).variant">
              <i :class="`fa fa-sm fa-${serverStatusBadge(selectedServer).icon} text-white`"></i>
              {{ serverStatusBadge(selectedServer).text }}
            </b-badge>
          </span>
        </b-list-group-item>
        <b-list-group-item class="d-flex align-items-center">
          Client Version
          <span class="ml-auto">
            <span v-if="selectedServer.client_version">
              <span class="font-size-xs">v</span>
              <span>{{ selectedServer.client_version }}</span>
            </span>
            <i
              v-else
              class="fa fa-minus fa-xs opacity-50" />
          </span>
        </b-list-group-item>
      </b-list-group>
    </b-form>

    <template #modal-footer>
      <div class="d-flex w-100">
        <b-button
          variant="outline-secondary"
          size="md"
          @click="hideModal('installRecipeModal')">
          Cancel
        </b-button>
        <b-button
          :disabled="!selectedRecipeVersionExist"
          :class="{ 'cursor-not-allowed' : !selectedRecipeVersionExist }"
          variant="primary"
          size="md"
          class="ml-auto"
          @click="forceToInstallationPage()">
          Submit
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { UNSELECT_SERVER } from '@/core/services/store/servers.module'
import {
  // actions
  GET_RECIPE_VERSIONS,

  // mutations
  SELECT_RECIPE,
  UNSELECT_RECIPE,
  SAVE_RECIPE_VERSION,
  CLEAR_RECIPE_VERSION,

  // defaults
  DEFAULT_SELECTED_RECIPE,
  DEFAULT_SELECTED_RECIPE_VERSION,
} from '@/core/services/store/recipes.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import { showModal, hideModal } from '@/core/utils'

export default {
  name: 'ModalInstallRecipe',
  data() {
    return {
      defaultSelectedRecipe: { ...DEFAULT_SELECTED_RECIPE },
      defaultSelectedRecipeVersion: { ...DEFAULT_SELECTED_RECIPE_VERSION },

      showModal,
      hideModal,
    }
  },
  computed: {
    ...mapGetters([
      'countryFlag',
      'countryName',

      'recipesGroupsList',
      'recipesGroupsEmpty',

      'recipesList',
      'recipesEmpty',
      'selectedRecipeId',
      'recipeVersions',
      'recipeVersionsPending',
      'selectedRecipeVersionId',
      'selectedRecipeVersionExist',
      'beautifiedVersionTitle',
      'recipesResponses',

      'selectedServer',
      'selectedServerId',
      'selectedServerExist',
      'serverStatusBadge',
      'serversResponses',
    ]),

    selectedRecipe: {
      get() {
        return this.$store.getters.selectedRecipe
      },
      set(recipe) {
        this.$store.commit(SELECT_RECIPE, recipe)
      },
    },

    selectedRecipeVersion: {
      get() {
        return this.$store.getters.selectedRecipeVersion
      },
      set(version) {
        this.$store.commit(SAVE_RECIPE_VERSION, version)
      },
    },
  },
  watch: {
    /* eslint-disable-next-line no-unused-vars */
    selectedRecipeId(newVal, oldVal) {
      if (newVal) {
        this.$store.dispatch(GET_RECIPE_VERSIONS, {
          id: newVal,
          isShowing: false,
        })

        this.$store.commit(CLEAR_RECIPE_VERSION)
      }
    }
  },
  mounted() {
    eventBus.$on('openModal', type => { this.showModal(type) })
  },
  destroyed() {
    eventBus.$off('openModal')
  },
  methods: {
    unselectServer() {
      this.$store.commit(UNSELECT_SERVER)
    },

    unselectRecipe() {
      this.$store.commit(UNSELECT_RECIPE)
    },

    unselectRecipeVersion() {
      this.$store.commit(CLEAR_RECIPE_VERSION)
    },

    forceToInstallationPage() {
      this.$router.push({ name: 'ServersRecipeInstall' }).catch(() => {})
    },
  }
}
</script>
